import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { googleIcon, SmsIcon } from '../../Utils/Images';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import FormInput from '../../Components/Ui/FormInput';
import FormCheckbox from '../../Components/FormFields/FormCheckbox';
import { useFormState, FormState, FormErrors } from '../../Hooks/Auth/useFormState';
import useFormValidation from '../../Hooks/Auth/useFormValidation';
import { useBrandAuthConfig } from '../../Hooks/Auth/useBrandAuthConfig';
import { useAuthFn } from '../../Hooks/Auth/useAuthFn';
import RightContentLogin from '../../Components/Auth/RightContentLogin';
import FormSeparator from '../../Components/FormFields/FormSeparator';
import usePhoneDialog from '../../Hooks/Auth/usePhoneDialog';
import ForgotPassword from '../../Components/Auth/ForgotPassword';
import TypeAccount from '../../Components/Auth/TypeAccount';
import { LayoutAuth } from '../../Components/Auth/LayoutAuth';
import { ENV_DECLARONS, ENV_VERIFIED_TRAINER, getActiveClientBrandConfig, isBrandActive } from '../../config/environment.config';
import ButtonField from '../../Components/Ui/ButtonField';
import DialogLayout from '../../Components/DialogLayout';

export interface FormInterface {
  email: string;
  password: string;
  remember: boolean;
  showPass: boolean;
  loading: boolean;
}

function LoginPage() {
  const { classes, common, brand, brandConfig } = useTailwindBrand();
  const primary = brandConfig.colors?.primary ?? '#0054A3';
  const { brandContent } = useBrandAuthConfig()
  const { validateField } = useFormValidation();
  const navigate = useNavigate();
  const courses = useSelector((state: any) => state?.punctualCourse?.activeCourse?.courses);
  const currentBrandContent = brandContent[brand as keyof typeof brandContent] || brandContent.teachr;
  const course: any = courses ? Object?.values(courses) : [];
  const { form, errors, setField, setErrors, clearError } = useFormState({
    email: '',
    password: '',
    remember: false,
    showPass: false,
    loading: false
  });

  const [googleDialogOpen, setGoogleDialogOpen] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const updatedForm = { ...form, [name]: value };

      setField(name as keyof FormState, value);

      // Validation en temps réel
      const error = validateField(name as keyof FormState, value, updatedForm);
      if (error) {
        setErrors({ ...errors, [name]: error });
      } else {
        clearError(name as keyof FormErrors);
      }
    },
    [form, setField, setErrors, clearError, validateField, errors]
  );
  // Validation complète du formulaire
  const validate = useCallback((): boolean => {
    const emailError = validateField('email', form.email, form);
    // Pour la connexion, on valide juste que le password n'est pas vide
    const passwordError = !form.password || form.password.trim() === ''
      ? 'Veuillez remplir ce champ'
      : '';
    const validationErrors: FormErrors = {};
    if (emailError) validationErrors.email = emailError;
    if (passwordError) validationErrors.password = passwordError;

    setErrors(validationErrors);
    return !emailError && !passwordError;
  }, [form, validateField, setErrors]);
  const setLoading = (value: boolean) => {
    setField("loading", value)
  }
  const { handleSetAndRedirectUserConnect, handleLoginWithMailAndPwd, handleLoginWithGoogle } = useAuthFn({
    validate,
    course,
    form,
    setLoading
  });
  const { showPhoneLoginWidget, phoneDialogNode } = usePhoneDialog({
    handleSetAndRedirectUserConnect: handleSetAndRedirectUserConnect,
    loading: form.loading ?? false
  });

  const handleGoogleClick = () => {
    setGoogleDialogOpen(true);
  };

  const handleGoogleRememberChoice = (value: boolean) => {
    setGoogleDialogOpen(false);
    handleLoginWithGoogle(value);
  };

  return (

    <LayoutAuth rightContent={<RightContentLogin />} >
      <div className='w-full lg:w-1/2 h-full flex flex-col justify-center'>
        <div className={` max-w-2xl mx-auto w-full  bg-white ${isBrandActive(ENV_DECLARONS) ? `${classes.primaryBorder} border` : `border border-white/80`} rounded-2xl overflow-hidden shadow-[0_24px_48px_-16px_rgba(15,23,42,0.35)] space-y-6`}>
          {/* En-tête en dégradé aux couleurs de la marque, aligné sur TypeAccount */}
          <div
            className="relative overflow-hidden p-6 md:p-8 flex-shrink-0 /text-center"
            style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}F2 60%, ${primary}D9 100%)` }}
          >
            <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }} />
            <div className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <h1 className={`relative ${common.subtitle} text-white font-extrabold mb-2`}>
              {currentBrandContent.title}
            </h1>
            <p className={`md:text-base lg:text-lg text-sm relative text-white font-semibold`}>
              {currentBrandContent.subtitle}
            </p>
          </div>
          <form onSubmit={handleLoginWithMailAndPwd} className='space-y-6 px-4 md:px-6'>
            {/* Champs du formulaire */}
            <div className='space-y-5'>
              <FormInput
                label="Adresse e-mail"
                name="email"
                type="email"
                placeholder="john.dupont@email.com"
                value={form.email}
                error={errors.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />

              <FormInput
                label="Mot de passe"
                name="password"
                type={form.showPass ? 'text' : 'password'}
                placeholder="Entrez votre mot de passe"
                value={form.password}
                error={errors.password}
                onChange={handleChange}
                showPasswordToggle
                showPassword={form.showPass}
                onTogglePassword={() => setField('showPass', !form.showPass)}
                required
                autoComplete="current-password"
              />
            </div>
            {/* Options de connexion */}
            <div className="flex /flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <FormCheckbox
                label="Se souvenir de moi"
                checked={form.remember ?? false}
                onChange={() => setField('remember', !form.remember)}
                required={false}
                classLabel={`${classes.text} font-bold`}
              />
              <ForgotPassword isLogin={true} />
            </div>
            <ButtonField
              label="Se connecter"
              variant="contained"
              className="font-bold w-full"
              onClick={handleLoginWithMailAndPwd}
              disabled={(form.email && form.password) ? form.loading : false}
              loading={(form.email && form.password) ? form.loading : false}
            />
            {/* Première connexion : élève ou client dont le compte a été créé à
                l'inscription à l'avance immédiate, sans mot de passe connu */}
            <div className={`flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-sm ${classes.text} ${classes.fontFamily}`}>
              <span>
                Vous êtes {getActiveClientBrandConfig("", "un ", "un ")} et c'est votre première connexion ?
              </span>
              <ForgotPassword isLogin={true} variant="firstLogin" />
            </div>
          </form>
          <div className=" px-4 md:px-6">
            <FormSeparator children={"Ou se connecter par"} />
          </div>
          {/* Méthodes de connexion alternatives */}
          <div className={`flex lg:flex-row flex-col items-center justify-center gap-2 lg:gap-x-8 px-4 md:px-6 `}>
            {[
              { iconSrc: googleIcon, alt: "Google Icon", title: 'Google', onClick: handleGoogleClick, class: ``, loading: (form.email && form.password) ? false : form.loading },
              { iconSrc: SmsIcon, alt: "SMS Icon", title: 'SMS', onClick: showPhoneLoginWidget, class: `${isBrandActive(ENV_VERIFIED_TRAINER) ? 'hidden' : ''}`, loading: false }]
              .map((btn, key) => (
                <ButtonField
                  key={key}
                  label={btn.title}
                  variant="outlined"
                  className={`font-bold w-full py-3 rounded-xl bg-white ${classes.primaryText} ${classes.primaryBorder} ${btn.class}`}
                  onClick={btn.onClick}
                  disabled={btn.loading}
                  loading={btn.loading}
                  startIcon={<img
                    src={btn.iconSrc}
                    alt={btn.alt}
                    className="w-6 h-6 group-hover:scale-110 transition-transform"
                  />}
                />
              ))}
          </div>
          {/* Section d'inscription */}
          <div className={`flex justify-center items-center mx-auto gap-x-2 ${classes.text} font-bold pb-4`}>
            Vous n'avez pas de compte ?{" "}<TypeAccount children="S'inscrire" navigate={navigate} />
          </div>
        </div>
      </div>
      <div id="recaptcha-container" />
      <DialogLayout
        open={googleDialogOpen}
        title="Se souvenir de moi"
        text="Souhaitez-vous rester connecté sur cet appareil ?"
        cancel="Non"
        valid="Oui"
        handleClose={() => handleGoogleRememberChoice(false)}
        handleCloseIcon={() => setGoogleDialogOpen(false)}
        handleSubmit={() => handleGoogleRememberChoice(true)}
      />
      {phoneDialogNode}
    </LayoutAuth >
  );
}

export default LoginPage;
